import {createSlice, type PayloadAction} from '@reduxjs/toolkit';

interface InitialData {
    countryCode: string;
    languageCode: string;
    isDarkMode: boolean;
    assetId: string;
    /** Pixels the host status bar covers at the top of the iframe. */
    topInset: number;
    /** Pixels the host menu floats over the bottom of the iframe. */
    menuInset: number;
}

interface ModuleDetails {
    isInitialized: boolean;
}

interface DeviceConnectionPayload {
    assetId: string;
    isConnected: boolean;
}

interface UwmState {
    initialData: InitialData | null;
    moduleDetails: ModuleDetails | null;
    deviceConnection: DeviceConnectionPayload | null;
    initializationError: string | null;
    isContentLoaded: boolean;
}

const initialState: UwmState = {
    initialData: null,
    moduleDetails: null,
    deviceConnection: null,
    initializationError: null,
    isContentLoaded: false,
};

const uwmSlice = createSlice({
    name: 'uwm',
    initialState,
    reducers: {
        setInitialData(state, action: PayloadAction<InitialData>) {
            state.initialData = action.payload;

            if (typeof document !== 'undefined') {
                document.getElementById('root')?.classList.toggle('dark', action.payload.isDarkMode);
                document.documentElement.style.setProperty('--top-inset', `${action.payload.topInset}px`);
                document.documentElement.style.setProperty('--menu-inset', `${action.payload.menuInset}px`);
            }
        },
        resetState(state) {
            state.initialData = null;
            state.moduleDetails = null;
            state.deviceConnection = null;
            state.initializationError = null;
            state.isContentLoaded = false;
        },
        setModuleDetails(state, action: PayloadAction<ModuleDetails>) {
            state.moduleDetails = action.payload;
        },
        setDeviceConnectionChanged(state, action: PayloadAction<DeviceConnectionPayload>) {
            state.deviceConnection = action.payload;
        },
        setDarkMode(state, action: PayloadAction<boolean>) {
            if (state.initialData) {
                state.initialData.isDarkMode = action.payload;
            }

            if (typeof document !== 'undefined') {
                document.getElementById('root')?.classList.toggle('dark', action.payload);
            }
        },
        setInitializationError(state, action: PayloadAction<string | null>) {
            state.initializationError = action.payload;
        },
        setContentLoaded(state, action: PayloadAction<boolean>) {
            state.isContentLoaded = action.payload;
        },
    },
});

export const {
    resetState,
    setInitialData,
    setModuleDetails,
    setDeviceConnectionChanged,
    setDarkMode,
    setInitializationError,
    setContentLoaded,
} = uwmSlice.actions;

export default uwmSlice.reducer;
